// Разбор вставленного куска книги на «фамилия» и «сумма».
//
// Сумма НЕ разбирается здесь: она уезжает на сервер строкой, как её вставили («51 869,40»,
// «51869.4», «51 869»). Разбор денег на клиенте — это второй разборщик рядом с серверным, и
// однажды они разойдутся на пробеле-разделителе разрядов. Здесь решается ровно одно: где в
// строке фамилия и где число.
//
// ## Две формы вставки, и вторая появилась 16.10.2026
//
// 1. **Целая таблица вместе со строкой шапки.** Так её копирует заказчик: колонки «№ п/п»,
//    «ФИО», «Пристава», «Всего Без НДФЛ», «Выдано», «Остаток» и ещё одиннадцать. Нужная сумма
//    стоит в «Всего Без НДФЛ», а ПОСЛЕДНЯЯ заполненная колонка — это «Остаток»: после выдач он
//    меньше. Раньше разбор брал последнюю и молча загружал остаток вместо официальной суммы.
//    Колонки находятся по шапке, а шапка у Excel — это ячейка с переводом строки внутри кавычек
//    («Дата выдачи \n15-30 число»), поэтому режется текст по правилам таблицы, а не по строкам.
// 2. **Две колонки или кусок без шапки**: фамилия первая, сумма последняя заполненная. Между
//    ними у заказчика стоят объект, должность и часы. Колонку-номер перед фамилией пропускаем.
//    Если в строках по нескольку чисел, а шапки нет, угадать колонку нельзя — об этом
//    говорит `несколькоЧисел`, и экран просит скопировать таблицу с шапкой.

import type { MatchLineIn } from '@/shared/api/endpoints/official';

/** Число в конце строки: цифры, пробелы-разделители разрядов, запятая или точка, минус. */
const ЧИСЛО_В_КОНЦЕ = /^(.*?)[\s\u00A0]+(-?[\d][\d\s\u00A0.,]*)$/;
const ПОХОЖЕ_НА_ЧИСЛО = /^-?\d[\d\s\u00A0.,]*$/;
const БУКВА = /[A-Za-zА-Яа-яЁё]/;
const ИТОГО = /^итого/;

/** Сколько верхних строк просматриваем в поисках шапки: у заказчика она занимает две. */
const СТРОК_ПОД_ШАПКУ = 12;

export interface РазборВставки {
  строки: MatchLineIn[];
  /** Колонки фамилии и суммы найдены по шапке таблицы, а не угаданы по положению. */
  поШапке: boolean;
  /**
   * Как называется колонка, из которой взяты суммы: «Всего Без НДФЛ». Человек видит это слово
   * над отчётом и может сверить с тем, что имел в виду, — выбор колонки по шапке эвристика, и
   * молча она не работает.
   */
  колонкаСуммы: string | null;
  /**
   * Шапки нет, а в строках по нескольку чисел: взята ПОСЛЕДНЯЯ колонка, и она может быть не
   * той, что нужна. Человеку это надо сказать до загрузки, а не после сверки.
   */
  несколькоЧисел: boolean;
}

export function разобратьВставку(текст: string): MatchLineIn[] {
  return разобратьВставкуСОтчётом(текст).строки;
}

export function разобратьВставкуСОтчётом(текст: string): РазборВставки {
  const таблица = разбитьНаЯчейки(текст);
  const шапка = найтиШапку(таблица);
  if (шапка !== null) {
    return {
      строки: строкиПоШапке(таблица, шапка),
      поШапке: true,
      колонкаСуммы: шапка.названиеСуммы,
      несколькоЧисел: false,
    };
  }
  return строкиБезШапки(таблица);
}

// ── Таблица из текста ─────────────────────────────────────────────────────────────────────

/**
 * Текст буфера в строки и ячейки. Excel разделяет ячейки табуляцией, а ячейку с переводом
 * строки или кавычкой берёт в кавычки (внутренняя кавычка удваивается). Резать такой текст по
 * переводам строк значит порвать шапку на куски и получить мусорные «фамилии».
 *
 * Кавычка открывает ячейку только в её начале: `Тарасов "Тарас" 100` остаётся как есть. Если
 * кавычку не закрыли до конца текста, это не таблица Excel, а обычный текст с кавычкой, и
 * разбор идёт заново без правил кавычек: иначе весь остаток вставки слипся бы в одну ячейку.
 */
function разбитьНаЯчейки(текст: string, сКавычками = true): string[][] {
  const нормальный = текст.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const строки: string[][] = [];
  let строка: string[] = [];
  let ячейка = '';
  let вКавычках = false;
  for (let i = 0; i < нормальный.length; i += 1) {
    const знак = нормальный[i]!;
    if (вКавычках) {
      if (знак !== '"') {
        ячейка += знак;
      } else if (нормальный[i + 1] === '"') {
        ячейка += '"';
        i += 1;
      } else {
        вКавычках = false;
      }
      continue;
    }
    if (знак === '"' && сКавычками && ячейка === '') {
      вКавычках = true;
    } else if (знак === '\t') {
      строка.push(ячейка);
      ячейка = '';
    } else if (знак === '\n') {
      строка.push(ячейка);
      строки.push(строка);
      строка = [];
      ячейка = '';
    } else {
      ячейка += знак;
    }
  }
  if (вКавычках) {
    return разбитьНаЯчейки(текст, false);
  }
  строка.push(ячейка);
  строки.push(строка);
  return строки
    .map((ячейки) => ячейки.map((кусок) => кусок.trim()))
    .filter((ячейки) => ячейки.some((кусок) => кусок !== ''));
}

function нормаль(текст: string): string {
  return текст
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[\s\u00A0]+/g, ' ')
    .trim();
}

// ── Вставка с шапкой ──────────────────────────────────────────────────────────────────────

interface Шапка {
  строка: number;
  имя: number;
  сумма: number;
  /** Подпись колонки суммы как в таблице, с пробелами в одну черту: «Всего Без НДФЛ». */
  названиеСуммы: string;
}

function этоКолонкаИмени(ячейка: string): boolean {
  // «ФИО», «ФИО сотрудника», «Фамилия имя отчество», «Ф.И.О.»
  return (
    ячейка.startsWith('фио') || ячейка.startsWith('фамилия') || /^ф\.? ?и\.? ?о\.?$/.test(ячейка)
  );
}

/**
 * Строка шапки: в ней есть и «ФИО», и «Всего …». Сумма — колонка «Всего»; если таких
 * несколько, та, что про «без НДФЛ»: именно её заказчик называет официальной суммой периода.
 * Слова «без НДФЛ» ищутся именно целиком: «Всего с НДФЛ» — это та же сумма до налога, и взять
 * её вместо официальной значило бы молча загрузить чужое число. Нет колонки «без НДФЛ» — берётся
 * первая «Всего», а её название человек видит над отчётом (`колонкаСуммы`) и может поправить.
 */
function найтиШапку(таблица: string[][]): Шапка | null {
  const предел = Math.min(таблица.length, СТРОК_ПОД_ШАПКУ);
  for (let номер = 0; номер < предел; номер += 1) {
    const ячейки = таблица[номер]!.map(нормаль);
    const имя = ячейки.findIndex(этоКолонкаИмени);
    if (имя < 0) {
      continue;
    }
    const суммы = ячейки.flatMap((ячейка, индекс) => (ячейка.includes('всего') ? [индекс] : []));
    if (суммы.length === 0) {
      continue;
    }
    const сумма = суммы.find((индекс) => ячейки[индекс]!.includes('без ндфл')) ?? суммы[0]!;
    const названиеСуммы = (таблица[номер]![сумма] ?? '').replace(/[\s\u00A0]+/g, ' ').trim();
    return { строка: номер, имя, сумма, названиеСуммы };
  }
  return null;
}

function строкиПоШапке(таблица: string[][], шапка: Шапка): MatchLineIn[] {
  const строки: MatchLineIn[] = [];
  for (const ячейки of таблица.slice(шапка.строка + 1)) {
    const имя = ячейки[шапка.имя] ?? '';
    // Вторая строка шапки («1 Неделя ₽», «Дата»…) и строка итогов фамилии не несут: у второй
    // имя пусто, у итогов слова «Итого» или тоже пусто. Строка с фамилией, но без суммы остаётся
    // и уезжает на сервер: он назовёт, что с ней не так, а молча выброшенная не восстановится.
    if (!БУКВА.test(имя) || ИТОГО.test(нормаль(имя)) || этоКолонкаИмени(нормаль(имя))) {
      continue;
    }
    строки.push({ name: имя, amount: ячейки[шапка.сумма] ?? '' });
  }
  return строки;
}

// ── Вставка без шапки ─────────────────────────────────────────────────────────────────────

function строкиБезШапки(таблица: string[][]): РазборВставки {
  const строки: MatchLineIn[] = [];
  let несколькоЧисел = false;
  for (const ячейки of таблица) {
    if (ячейки.length === 1) {
      const строка = разобратьСтрокуБезТабуляции(ячейки[0]!);
      if (строка.name !== '' && !ИТОГО.test(нормаль(строка.name))) {
        строки.push(строка);
      }
      continue;
    }
    // Колонка-номер перед фамилией не фамилия: имя — первая ячейка, где есть буквы.
    const позиция = ячейки.findIndex((ячейка) => БУКВА.test(ячейка));
    if (позиция < 0 || ИТОГО.test(нормаль(ячейки[позиция]!))) {
      continue;
    }
    const заполненные = ячейки.slice(позиция + 1).filter((ячейка) => ячейка !== '');
    if (заполненные.filter((ячейка) => ПОХОЖЕ_НА_ЧИСЛО.test(ячейка)).length > 1) {
      несколькоЧисел = true;
    }
    строки.push({ name: ячейки[позиция]!, amount: заполненные[заполненные.length - 1] ?? '' });
  }
  return { строки, поШапке: false, колонкаСуммы: null, несколькоЧисел };
}

function разобратьСтрокуБезТабуляции(строка: string): MatchLineIn {
  const совпадение = ЧИСЛО_В_КОНЦЕ.exec(строка);
  if (совпадение === null) {
    // Суммы в строке нет вовсе. Строка всё равно уезжает на сервер: он скажет, что с ней не
    // так, поимённо. Молча выброшенная строка не восстанавливается ничем.
    return { name: строка, amount: '' };
  }
  return { name: совпадение[1]!.trim(), amount: совпадение[2]!.trim() };
}
